"use client";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Shield, Eye, Lock, Users, FileText, Mail } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-16 sm:py-24 relative overflow-hidden pt-32 flex items-center min-h-96">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20 animate-pulse"></div>
          <div
            className="absolute -bottom-16 -left-16 w-48 h-48 bg-indigo-200 rounded-full opacity-15 animate-bounce"
            style={{ animationDuration: "3s" }}
          ></div>
          <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-purple-400 rounded-full animate-ping"></div>
          <div
            className="absolute top-1/3 right-1/3 w-2 h-2 bg-indigo-400 rounded-full animate-ping"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Privacy <span className="text-purple-600">Policy</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Your privacy is important to us. Learn how we protect and handle
              your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Icons */}
      <section className="py-16 relative -mt-8 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Data Protection</h3>
              <p className="text-gray-600 text-sm">
                Your data is secured with industry-standard encryption
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Eye className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600 text-sm">
                Clear information about how we use your data
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Your Rights</h3>
              <p className="text-gray-600 text-sm">
                Full control over your personal information
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last updated:</strong>{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="w-6 h-6 text-purple-600 mr-2" />
                    Information We Collect
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We collect information you provide directly to us when
                      you:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Create an account or register for our services</li>
                      <li>Book a consultation or mentoring session</li>
                      <li>
                        Contact us through our website or support channels
                      </li>
                      <li>
                        Subscribe to our newsletter or marketing communications
                      </li>
                      <li>
                        Participate in surveys, contests, or promotional
                        activities
                      </li>
                    </ul>
                    <p>
                      This information may include your name, email address,
                      phone number, company information, and any other details
                      you choose to provide.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="w-6 h-6 text-purple-600 mr-2" />
                    How We Use Your Information
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>We use the information we collect to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide, maintain, and improve our services</li>
                      <li>
                        Process bookings and facilitate mentoring sessions
                      </li>
                      <li>
                        Send you technical notices, updates, security alerts,
                        and support messages
                      </li>
                      <li>
                        Respond to your comments, questions, and customer
                        service requests
                      </li>
                      <li>
                        Send you marketing communications about our services
                        (with your consent)
                      </li>
                      <li>
                        Monitor and analyze trends, usage, and activities in
                        connection with our services
                      </li>
                      <li>
                        Comply with legal obligations and protect our legal
                        rights
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-6 h-6 text-purple-600 mr-2" />
                    Information Sharing and Disclosure
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We do not sell, trade, or otherwise transfer your personal
                      information to third parties without your consent, except
                      in the following circumstances:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Service Providers:</strong> We may share
                        information with trusted third-party service providers
                        who assist us in operating our website and providing our
                        services
                      </li>
                      <li>
                        <strong>Mentors:</strong> We share necessary information
                        with our certified mentors to facilitate your booked
                        sessions
                      </li>
                      <li>
                        <strong>Legal Requirements:</strong> We may disclose
                        information if required by law or in response to valid
                        requests by public authorities
                      </li>
                      <li>
                        <strong>Business Transfers:</strong> Information may be
                        transferred in connection with a merger, acquisition, or
                        sale of business assets
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Lock className="w-6 h-6 text-purple-600 mr-2" />
                    Data Security
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We implement appropriate technical and organizational
                      security measures to protect your personal information
                      against unauthorized access, alteration, disclosure, or
                      destruction. These measures include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Encryption of sensitive data in transit and at rest
                      </li>
                      <li>Regular security assessments and updates</li>
                      <li>Access controls and authentication measures</li>
                      <li>Employee training on data protection practices</li>
                    </ul>
                    <p>
                      However, no method of transmission over the Internet or
                      electronic storage is 100% secure. While we strive to
                      protect your personal information, we cannot guarantee its
                      absolute security.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Eye className="w-6 h-6 text-purple-600 mr-2" />
                    Your Rights and Choices
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      You have certain rights regarding your personal
                      information:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Access:</strong> You can request access to your
                        personal information we hold
                      </li>
                      <li>
                        <strong>Correction:</strong> You can request correction
                        of inaccurate or incomplete information
                      </li>
                      <li>
                        <strong>Deletion:</strong> You can request deletion of
                        your personal information, subject to certain exceptions
                      </li>
                      <li>
                        <strong>Portability:</strong> You can request a copy of
                        your personal information in a structured,
                        machine-readable format
                      </li>
                      <li>
                        <strong>Opt-out:</strong> You can opt-out of marketing
                        communications at any time
                      </li>
                    </ul>
                    <p>
                      To exercise these rights, please contact us using the
                      information provided below.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Cookies and Tracking Technologies
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We use cookies and similar tracking technologies to
                      collect and track information about your use of our
                      website. Cookies are small data files stored on your
                      device. We use cookies for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Essential website functionality</li>
                      <li>Analytics to understand how our website is used</li>
                      <li>Personalization of your experience</li>
                      <li>
                        Marketing and advertising purposes (with your consent)
                      </li>
                    </ul>
                    <p>
                      You can control cookies through your browser settings, but
                      please note that disabling cookies may affect the
                      functionality of our website.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Data Retention
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We retain your personal information for as long as
                      necessary to provide our services and fulfill the purposes
                      outlined in this privacy policy. We will also retain
                      information as required by law or to resolve disputes and
                      enforce our agreements.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Changes to This Privacy Policy
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We may update this privacy policy from time to time. We
                      will notify you of any changes by posting the new privacy
                      policy on this page and updating the &quot;Last
                      updated&quot; date. We encourage you to review this
                      privacy policy periodically.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Mail className="w-6 h-6 text-purple-600 mr-2" />
                    Contact Us
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      If you have any questions about this privacy policy or our
                      data practices, please contact us:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Email:</strong> privacy@theentercountant.com
                      </p>
                      <p>
                        <strong>Address:</strong> TheEnterCountant, Belgium
                      </p>
                      <p>
                        <strong>Phone:</strong> +32 (0) 123 456 789
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
